import React from "react";
import { Sparkles } from "lucide-react";

/**
 * ServiceOptionCard — large selectable card, styled to match the Taxi
 * class picker in RideRequest.jsx (photo → name → meta → price → ETA).
 *
 * Dumb / presentational. Selection is bubbled up via
 * `onSelect(option_code)`. No pricing math here.
 *
 * Props:
 *   option: {
 *     option_code:  string,   // canonical id sent to /fare/estimate
 *     name?:        string,   // display label
 *     description?: string,   // small subtitle (seats, capacity, …)
 *     image?:       string,   // optional photo URL (matches Taxi cards)
 *     icon?:        React.ReactNode, // fallback when image missing
 *     price?:       number|null,
 *     currency?:    string,
 *     eta?:         string,   // "2 min away"
 *     badge?:       string|null, // "VIP" / "Popular"
 *   }
 *   selected: boolean
 *   onSelect: (option_code) => void
 *   fmtFare?: (n, cur) => string
 *   testId?: string
 */
export function ServiceOptionCard({ option, selected, onSelect, fmtFare, testId }) {
  const { option_code, name, description, image, icon, price, currency, eta, badge } = option || {};
  const displayName = name || option_code;
  const formatted = price != null && typeof fmtFare === "function"
    ? fmtFare(price, currency)
    : (price != null ? `${price.toFixed(2)} ${(currency || "").toUpperCase()}` : null);

  return (
    <button
      type="button"
      onClick={() => onSelect(option_code)}
      className={`snap-center flex-shrink-0 w-[128px] rounded-2xl overflow-hidden border transition-all duration-200 relative text-start ${
        selected
          ? "bg-amber-gold/[0.08] border-amber-gold ring-2 ring-amber-gold/40 scale-[1.02]"
          : "bg-white/[0.04] border-white/10 hover:border-white/25"
      }`}
      data-testid={testId || `service-option-${option_code}`}
      aria-pressed={selected}
    >
      {badge && (
        <div className="absolute top-1.5 end-1.5 z-10 text-[8px] font-bold uppercase tracking-widest text-amber-gold flex items-center gap-0.5 bg-black/60 rounded-full px-1.5 py-0.5">
          <Sparkles size={8} /> {badge}
        </div>
      )}

      {/* Header image / icon area — full 4:3 photo when an image URL is
          provided (Taxi-style); a compact 44px icon strip otherwise so
          categories without artwork (towing/moving/etc.) render tighter
          cards without a giant empty grey block. */}
      {image ? (
        <div className="w-full aspect-[4/3] bg-white/[0.06] border-b border-white/10 flex items-center justify-center overflow-hidden">
          <img src={image} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-full h-11 flex items-center justify-center border-b border-white/10">
          {icon || (
            <div className="w-6 h-6 rounded-full bg-white/[0.06] border border-white/10" aria-hidden />
          )}
        </div>
      )}

      {/* Body */}
      <div className="p-2.5">
        <div className="text-[12px] font-bold text-white truncate leading-tight">{displayName}</div>
        {description && (
          <div className="text-[10px] text-white/50 mt-0.5 truncate">{description}</div>
        )}
        {formatted && (
          <div
            className="mt-1.5 text-[13px] font-bold text-white tabular-nums"
            data-testid={`service-option-fare-${option_code}`}
          >
            {formatted}
          </div>
        )}
        {eta && <div className="text-[10px] text-white/50 mt-0.5">{eta}</div>}
      </div>
    </button>
  );
}
